import {
  Button,
  breakpoints,
  Col,
  DatePicker,
  Dialog,
  Icon,
  Input,
  Text,
  TimePicker,
  Toast,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import { act, read } from "../../net/rally.ts";
import { coalesced } from "../activity-refresh.ts";
import { type Held, landed, opening } from "../held.ts";
import { shelfFor } from "../kept.ts";
import { AddSomebody } from "../person/add-somebody.tsx";
import { Faces } from "../person/faces.tsx";
import { Handles } from "../person/handles.tsx";
import { CONTENT_HEADER_HEIGHT, GUTTER } from "../reading.ts";
import { useLive } from "../run/live.ts";
import { ConversationIcon } from "./conversation-icon.tsx";
import { type Somebody, titleOf } from "./title.ts";

type PutAway = {
  snoozedUntil: string | null;
  archivedForMe: boolean;
  archivedForAll: boolean;
  pinnedForMe: boolean;
  pinnedForAll: boolean;
  mutedForMe: boolean;
};

type ConversationHead = {
  id: string;
  title: string | null;
  description: string | null;
  icon: string | null;
  namedBy: string | null;

  private: boolean;
  away: PutAway;
};

type PersonInConversation = Somebody & { avatar: string | null; role?: string };

function pickedInstant(day: string, clock: string): Date | null {
  const [year, month, date] = day.split("-").map(Number);
  const [hour, minute] = clock.split(":").map(Number);
  if ([year, month, date, hour, minute].some((part) => part === undefined || Number.isNaN(part))) return null;
  return new Date(year ?? 0, (month ?? 1) - 1, date ?? 1, hour ?? 0, minute ?? 0, 0, 0);
}

function dayOf(when: Date): string {
  return `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, "0")}-${String(when.getDate()).padStart(2, "0")}`;
}

function saidWhy(cause: unknown): string {
  return failureSaid(cause, say("head.broke"));
}

const heads = shelfFor<ConversationHead>();

export function Head({
  conversation,
  me,
  people,
  onDetails,
  detailsShowing,
  onRoster,
  onCall,
}: {
  conversation: string;

  me: string;

  people: readonly PersonInConversation[];

  onDetails: () => void;

  detailsShowing: boolean;

  onRoster?: () => void;

  onCall?: ((withCamera: boolean) => void) | undefined;
}) {
  const theme = useTheme();

  const roomy = !theme.isAtMost(breakpoints.phone);

  const [held, setHeld] = useState<Held<ConversationHead>>(() => opening(conversation));
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [picking, setPicking] = useState(false);

  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState(dayOf(new Date()));
  const [clock, setClock] = useState("09:00");

  const [said, setSaid] = useState<{ text: string; wrong: boolean } | null>(null);

  const again = useCallback(() => {
    read<ConversationHead>("conversation.head", { conversation })
      .then((said) => {
        heads.keep(conversation, said);
        setHeld((was) => landed(was, { for: conversation, state: "read", what: said }));
      })
      .catch((cause: unknown) => {
        heads.forget(conversation);
        const why = failureSaid(cause, say("chat.broke"));
        setHeld((was) => landed(was, { for: conversation, state: "refused", why }));
      });
  }, [conversation]);

  useEffect(() => {
    setHeld(opening(conversation));
    setRenaming(false);
    setPicking(false);
    setAdding(false);
    setSaid(null);
    again();
  }, [conversation, again]);

  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.conversation === conversation) refresh.notice();
  });

  const tell = (text: string, wrong = false) => {
    setSaid({ text, wrong });
    setTimeout(() => setSaid(null), 2400);
  };

  const doing = async (what: () => Promise<unknown>) => {
    try {
      await what();
      again();
    } catch (cause) {
      tell(saidWhy(cause), true);
    }
  };

  const showing = heads.drawn(held, conversation);
  const head = showing.state === "read" ? showing.what : null;
  const refusal = showing.state === "refused" ? showing.why : null;
  const opened = showing.state === "read";

  const snoozeUntil = async (until: Date) =>
    doing(() => act("attention.snooze", { target: { kind: "conversation", id: conversation }, until }));

  const snoozeAtPicked = async () => {
    const until = pickedInstant(day, clock);
    if (!until || until.getTime() <= Date.now()) {
      tell(say("snooze.pickPast"), true);
      return;
    }
    setPicking(false);
    await snoozeUntil(until);
  };

  const mayAdd = people.some((one) => one.account === me && (one.role === "owner" || one.role === "admin"));

  const rename = async () => {
    const wanted = name.trim();
    if (!wanted) {
      setRenaming(false);
      return;
    }
    setRenaming(false);
    await doing(() => act("conversation.label", { conversation, title: wanted }));
  };

  return (
    <Col
      testID="conversation-head"
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],

        height: CONTENT_HEADER_HEIGHT,
        paddingHorizontal: GUTTER,
        borderBottomWidth: 1,
        borderBottomColor: theme.color["border.default"],
      }}
    >
      <Col
        testID="conversation-glyph"
        style={{
          width: 40,
          height: 40,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: theme.radius.md,
          backgroundColor: theme.color.accentSoft,
        }}
      >
        <ConversationIcon icon={head?.icon ?? null} title={head?.title ?? null} size={20} color="accent" />
      </Col>

      <Col style={{ flex: 1, minWidth: 0 }}>
        <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
          <Text
            typeName="bodySemibold"
            color="text.primary"
            numberOfLines={1}
            testID="conversation-name"
            style={{ flexShrink: 1 }}
          >
            {head
              ? titleOf({ title: head.title, people }, me)
              : refusal
                ? say("chat.noEntry")
                : say("head.opening")}
          </Text>
          {head?.private ? <Icon name="lock" size={14} color="text.tertiary" /> : null}
        </Col>
        {head?.description?.trim() || refusal || !opened ? (
          <Text typeName="caption" color="text.tertiary" numberOfLines={1} testID="conversation-about">
            {head?.description?.trim() ? head.description : refusal ? refusal : say("head.openingAbout")}
          </Text>
        ) : (
          <Col testID="conversation-about" style={{ paddingTop: 2 }}>
            <Faces people={people} me={me} size={16} cap={6} testID="head-faces" />
          </Col>
        )}
      </Col>
      <Handles
        conversation={conversation}
        head={head}
        roomy={roomy}
        doing={doing}
        tell={tell}
        onDetails={onDetails}
        detailsShowing={detailsShowing}
        mayAdd={mayAdd}
        onRename={() => {
          setName(head?.title ?? "");
          setRenaming(true);
        }}
        onAdd={() => setAdding(true)}
        onPickATime={() => setPicking(true)}
        onCall={onCall}
      />

      <Dialog
        visible={renaming}
        onDismiss={() => setRenaming(false)}
        title={say("head.renameTitle")}
        testID="rename-conversation"
        footer={
          <Button onPress={() => void rename()} testID="rename-save">
            {say("head.save")}
          </Button>
        }
      >
        <Input
          value={name}
          onChangeText={setName}
          placeholder={say("head.namePlaceholder")}
          autoFocus
          testID="rename-name"
          {...({
            onKeyPress: (event: KeyboardEvent) => {
              if (event.key === "Enter") void rename();
            },
          } as object)}
        />
      </Dialog>

      <AddSomebody
        conversation={conversation}
        visible={adding}
        here={people.map((one) => one.account)}
        me={me}
        mayAdd={mayAdd}
        onDismiss={() => setAdding(false)}
        onAdded={(many) => {
          tell(say("add.added")(many));
          onRoster?.();
        }}
      />

      <Dialog
        visible={picking}
        onDismiss={() => setPicking(false)}
        title={say("snooze.pickTitle")}
        testID="snooze-pick"
        footer={
          <Button onPress={() => void snoozeAtPicked()} testID="snooze-pick-save">
            {say("snooze.pickSave")}
          </Button>
        }
      >
        <DatePicker value={day} onChange={setDay} />
        <TimePicker value={clock} onChange={setClock} />
      </Dialog>

      {said ? (
        <Toast tone={said.wrong ? "danger" : "success"} visible>
          {said.text}
        </Toast>
      ) : null}
    </Col>
  );
}
